import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { capturePosthogEvent } from "config/posthog-runtime";
import { ANALYTICS_EVENTS } from "analytics";
import {
  HEALTH_CHECK_TRIGGER_EVENT,
  HEALTH_DOWN_POLL_BASE_MS,
  HEALTH_DOWN_POLL_MAX_MS,
  HEALTH_TRIGGER_DEBOUNCE_MS,
  LOADING_STUCK_FAILURE_THRESHOLD,
  OFFLINE_GRACE_MS,
} from "components/common/ErrorHandler/ErrorHandler.constants";
import { HEALTH_QUERY_KEY, useHealthCheck } from "hooks/useHealthCheck";
import type { HealthCheckResult } from "hooks/useHealthCheck/useHealthCheck.types";
import type {
  BackendStatus,
  UseErrorHandlerConnectivityResult,
} from "./useErrorHandlerConnectivity.types";

const UNKNOWN_HEALTH_RESULT: HealthCheckResult = {
  status: "unknown",
  ok: false,
};

function healthResultToConnectivityState(
  healthData: HealthCheckResult | undefined,
  isOnline: boolean
): { isMaintenance: boolean; isBackendAvailable: boolean; lastError: Error | undefined } {
  const statusCode = healthData?.statusCode;
  const status = healthData?.status;
  const isBackendError =
    statusCode !== undefined && statusCode >= 500 && statusCode < 600 && statusCode !== 503;
  const isNetworkError = status === "unknown";
  const isMaintenance = statusCode === 503 || status === "maintenance";
  const rawBackendAvailable = healthData === undefined ? true : healthData.ok;
  const lastError =
    isNetworkError || isBackendError
      ? new Error(isBackendError ? "Service error" : "Backend unavailable")
      : undefined;
  return {
    isMaintenance,
    isBackendAvailable: isOnline && rawBackendAvailable,
    lastError,
  };
}

function computeDownPollDelay(failureCount: number): number {
  const exponent = Math.max(0, failureCount - 1);
  const delay = HEALTH_DOWN_POLL_BASE_MS * Math.pow(2, exponent);
  return Math.min(delay, HEALTH_DOWN_POLL_MAX_MS);
}

function connectivityReason(args: {
  isOnline: boolean;
  isMaintenance: boolean;
  isBackendAvailable: boolean;
}): "offline" | "maintenance" | "service_unavailable" {
  if (!args.isOnline) return "offline";
  if (args.isMaintenance) return "maintenance";
  return "service_unavailable";
}

export const useErrorHandlerConnectivity = (): UseErrorHandlerConnectivityResult => {
  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator !== "undefined" && navigator.onLine
  );
  // Mirrors isOnline for the window listeners, which are registered once.
  const isOnlineRef = useRef(isOnline);
  const wasDownRef = useRef(false);
  const downSinceRef = useRef<number | null>(null);
  const shownReasonRef = useRef<string | null>(null);

  const {
    data: healthData,
    isError: healthCheckError,
    refetch,
    isFetching,
    failureCount,
    dataUpdatedAt,
  } = useHealthCheck({ enabled: true });

  const effectiveHealthData: HealthCheckResult | undefined =
    healthData ?? (healthCheckError ? UNKNOWN_HEALTH_RESULT : undefined);

  const { isMaintenance, isBackendAvailable, lastError } = healthResultToConnectivityState(
    effectiveHealthData,
    isOnline
  );
  const maintenanceDetails = effectiveHealthData?.maintenance;

  const isDown = !isOnline || !isBackendAvailable || isMaintenance;

  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  const lastTriggerAtRef = useRef<number>(0);
  const triggerProbe = useCallback(
    (force = false) => {
      const now = Date.now();
      const sinceLastTrigger = now - lastTriggerAtRef.current;
      const sinceLastResponse = dataUpdatedAt ? now - dataUpdatedAt : Number.POSITIVE_INFINITY;
      if (
        !force &&
        (sinceLastTrigger < HEALTH_TRIGGER_DEBOUNCE_MS ||
          sinceLastResponse < HEALTH_TRIGGER_DEBOUNCE_MS)
      ) {
        return;
      }
      lastTriggerAtRef.current = now;
      refetchRef.current();
    },
    [dataUpdatedAt]
  );

  const handleCheckNow = useCallback(() => {
    if (isFetching) return;
    capturePosthogEvent(ANALYTICS_EVENTS.APP_ERROR_RETRY_CLICKED, {
      reason: connectivityReason({ isOnline, isMaintenance, isBackendAvailable }),
      action: "health_check",
    });
    lastTriggerAtRef.current = Date.now();
    void refetchRef.current();
  }, [isFetching, isOnline, isMaintenance, isBackendAvailable]);

  const handleBackendError = useCallback(() => {
    triggerProbe();
  }, [triggerProbe]);

  const detectLoadingStuck = useCallback(
    () => isOnline && !isBackendAvailable && failureCount > LOADING_STUCK_FAILURE_THRESHOLD,
    [isOnline, isBackendAvailable, failureCount]
  );

  const getBackendStatus = useCallback(
    (): BackendStatus => ({
      isOnline,
      isBackendAvailable,
      lastError,
      retryCount: failureCount,
    }),
    [isOnline, isBackendAvailable, lastError, failureCount]
  );

  useEffect(() => {
    if (isDown) {
      if (!wasDownRef.current) {
        wasDownRef.current = true;
        downSinceRef.current = Date.now();
        const reason = connectivityReason({ isOnline, isMaintenance, isBackendAvailable });
        shownReasonRef.current = reason;
        capturePosthogEvent(ANALYTICS_EVENTS.APP_ERROR_SHOWN, {
          reason,
          visibility_state: typeof document !== "undefined" ? document.visibilityState : "unknown",
        });
      }
      return;
    }

    if (wasDownRef.current) {
      wasDownRef.current = false;
      const durationMs = downSinceRef.current ? Date.now() - downSinceRef.current : undefined;
      capturePosthogEvent(ANALYTICS_EVENTS.APP_ERROR_RECOVERED, {
        reason: shownReasonRef.current ?? "service_unavailable",
        duration_ms: durationMs,
      });
      downSinceRef.current = null;
      shownReasonRef.current = null;

      void queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key) || key.length === 0) return true;
          return key[0] !== HEALTH_QUERY_KEY[0];
        },
      });
    }
  }, [isDown, isOnline, isMaintenance, isBackendAvailable, queryClient]);

  useEffect(() => {
    const handleHealthCheckTrigger = () => {
      if (!isOnline) return;
      triggerProbe();
    };
    window.addEventListener(HEALTH_CHECK_TRIGGER_EVENT, handleHealthCheckTrigger);
    return () => {
      window.removeEventListener(HEALTH_CHECK_TRIGGER_EVENT, handleHealthCheckTrigger);
    };
  }, [isOnline, triggerProbe]);

  useEffect(() => {
    let offlineTimerId: number | null = null;
    const clearOfflineTimer = () => {
      if (offlineTimerId !== null) {
        window.clearTimeout(offlineTimerId);
        offlineTimerId = null;
      }
    };

    const handleOnline = () => {
      clearOfflineTimer();
      // A drop shorter than the grace window never showed anything; no probe needed.
      if (isOnlineRef.current) return;
      isOnlineRef.current = true;
      setIsOnline(true);
      lastTriggerAtRef.current = Date.now();
      refetchRef.current();
    };
    const handleOffline = () => {
      if (offlineTimerId !== null || !isOnlineRef.current) return;
      offlineTimerId = window.setTimeout(() => {
        offlineTimerId = null;
        if (typeof navigator !== "undefined" && navigator.onLine) return;
        isOnlineRef.current = false;
        setIsOnline(false);
      }, OFFLINE_GRACE_MS);
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      clearOfflineTimer();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!isDown) return;
      lastTriggerAtRef.current = Date.now();
      refetchRef.current();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isDown]);

  useEffect(() => {
    if (!isDown || !isOnline) return;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;

    const delay = computeDownPollDelay(failureCount);
    const timerId = window.setTimeout(() => {
      lastTriggerAtRef.current = Date.now();
      refetchRef.current();
    }, delay);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [isDown, isOnline, failureCount, dataUpdatedAt]);

  return {
    isOnline,
    isBackendAvailable,
    isMaintenance,
    maintenanceDetails,
    lastError,
    failureCount,
    isFetching,
    handleCheckNow,
    handleBackendError,
    refetch,
    detectLoadingStuck,
    getBackendStatus,
  };
};
